import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { WarningOctagon, Timer, CheckCircle, Ticket, TrendUp, EnvelopeSimple, CheckCircle as Check, XCircle, Circle, UserMinus, PauseCircle, ClockCountdown, ListChecks, CaretUp, CaretDown, FileCsv, ArrowBendUpLeft } from "@phosphor-icons/react";
import { useChartColors } from "@/lib/useChartColors";
import { toast } from "sonner";
import { humanTime, priorityColor, statusColor, timeUntil, fmtIST } from "@/lib/utils";

const PRIO_COLORS = { P1: "#ef4444", P2: "#f59e0b", P3: "#38bdf8", P4: "#a1a1aa" };
const TYPE_COLORS = { "Incident": "#38bdf8", "Service Request": "#22c55e", "Proactive Tasks": "#a78bfa", "Security Incident": "#ef4444" };
const TYPE_ORDER = ["Incident", "Service Request", "Proactive Tasks", "Security Incident"];

function timeAgo(iso) {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const Metric = ({ label, value, hint, icon: Icon, tone = "", to }) => {
  const inner = (
    <Card className={`p-5 border border-border bg-card/60 rounded-sm relative h-full ${to ? "cursor-pointer hover:border-primary/60 transition-colors" : ""}`} data-testid={`metric-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono leading-tight">{label}</div>
        {Icon && <Icon size={18} className="text-muted-foreground shrink-0" />}
      </div>
      <div className={`font-display text-2xl xl:text-3xl font-bold leading-none tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-2 font-mono">{hint}</div>}
    </Card>
  );
  return to ? <Link to={to} className="block" data-testid={`metric-link-${label.toLowerCase().replace(/\s/g,'-')}`}>{inner}</Link> : inner;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [mailHealth, setMailHealth] = useState(null);
  const [myOpen, setMyOpen] = useState([]);
  const [agentPerf, setAgentPerf] = useState([]);
  const [perfSort, setPerfSort] = useState({ key: "open", dir: "desc" });
  const [perfRange, setPerfRange] = useState("all");
  const [dashScope, setDashScope] = useState("all");  // admin-only: 'all' | 'team'
  const [dashType, setDashType] = useState(() => localStorage.getItem("trak_dash_type") || "all");  // 'all' | 'Incident' | 'Service Request' | 'Proactive Tasks'
  const chart = useChartColors();
  const TYPE_PILLS = [["all", "All"], ["Incident", "Incidents"], ["Service Request", "Service Requests"], ["Proactive Tasks", "Proactive"]];
  const typeWord = dashType === "all" ? "Tickets" : dashType === "Proactive Tasks" ? "Proactive Tasks" : `${dashType}s`;

  const loadPerf = (range, scope) => {
    const params = new URLSearchParams();
    if (range !== "all") {
      const days = parseInt(range, 10);
      params.set("date_from", new Date(Date.now() - days * 86400000).toISOString().slice(0, 10));
    }
    if (scope === "team") params.set("scope", "team");
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/reports/agent-performance${qs}`).then(r => setAgentPerf(r.data)).catch(() => {});
  };
  const loadSummary = (scope, tt = dashType) => {
    const p = new URLSearchParams();
    if (scope === "team") p.set("scope", "team");
    if (tt && tt !== "all") p.set("ticket_type", tt);
    const qs = p.toString() ? `?${p.toString()}` : "";
    api.get(`/reports/summary${qs}`).then(r => setData(r.data)).catch(() => {});
  };
  const setRange = (r) => { setPerfRange(r); loadPerf(r, dashScope); };
  const setScope = (s) => { setDashScope(s); loadSummary(s); loadPerf(perfRange, s); };
  const setType = (t) => { setDashType(t); localStorage.setItem("trak_dash_type", t); loadSummary(dashScope, t); };

  useEffect(() => {
    loadSummary("all", dashType);
    api.get("/reports/notifications-activity").then(r => setNotif(r.data)).catch(() => {});
    api.get("/reports/mail-health").then(r => setMailHealth(r.data)).catch(() => {});
    api.get("/reports/my-open").then(r => setMyOpen(r.data)).catch(() => {});
    loadPerf("all", "all");
  }, []);

  const openDay = async (date) => {
    if (!date) return;
    setDayDetail({ date, loading: true, rows: [] });
    try {
      const body = { group_by: "status", date_from: date, date_to: date };
      if (data?.scope === "self" && user?.id) body.assignee_id = user.id;
      const { data: r } = await api.post("/reports/custom", body);
      setDayDetail({ date, loading: false, rows: r.rows || [] });
    } catch (e) {
      setDayDetail({ date, loading: false, rows: [] });
    }
  };

  if (!data) return <div className="p-6 text-muted-foreground">Loading operations…</div>;

  const prioData = Object.entries(data.by_priority || {}).map(([k,v]) => ({ name: k, value: v, fill: PRIO_COLORS[k] }));
  const byType = data.by_type || {};
  const typeTotal = Object.values(byType).reduce((a, b) => a + b, 0);
  const typeData = TYPE_ORDER.filter(t => byType[t]).map(t => ({ name: t, value: byType[t], fill: TYPE_COLORS[t] }));
  const self = data.scope === "self";

  const PERF_COLS = [
    { key: "name", label: "Agent", align: "left", num: false },
    { key: "assigned", label: "Assigned", align: "right", num: true },
    { key: "open", label: "Open", align: "right", num: true },
    { key: "on_hold", label: "On Hold", align: "right", num: true },
    { key: "resolved", label: "Resolved", align: "right", num: true },
    { key: "breached", label: "Breached", align: "right", num: true },
    { key: "avg_response_hrs", label: "Avg Resp (h)", align: "right", num: true },
    { key: "avg_resolution_hrs", label: "Avg Resl (h)", align: "right", num: true },
    { key: "avg_achieved_resolution_hrs", label: "Avg Achieved (h)", align: "right", num: true },
    { key: "sla_compliance", label: "SLA %", align: "right", num: true },
  ];
  const toggleSort = (key) => setPerfSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" });
  const sortedPerf = [...agentPerf].sort((a, b) => {
    const { key, dir } = perfSort;
    const va = a[key], vb = b[key];
    const cmp = typeof va === "string" ? String(va).localeCompare(String(vb)) : (va - vb);
    return dir === "asc" ? cmp : -cmp;
  });
  const exportPerfCsv = () => {
    const header = PERF_COLS.map(c => c.label);
    const lines = [header, ...sortedPerf.map(a => [a.name, a.assigned, a.open, a.on_hold, a.resolved, a.breached, a.avg_response_hrs, a.avg_resolution_hrs, a.avg_achieved_resolution_hrs, a.sla_compliance])];
    const csv = lines.map(l => l.map(c => `"${String(c ?? "").replace(/"/g, "'")}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a"); link.href = url; link.download = `agent-performance-${perfRange}-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
    toast.success("Agent performance exported");
  };

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-view">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">{self ? "My Performance" : "Operations Overview"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">{self ? "My Command Deck" : "Command Deck"}</h1>
          {self && <div className="text-xs text-muted-foreground mt-1 font-mono">Personal metrics for {user?.name || "you"} · Unassigned shows the shared queue</div>}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 border border-border rounded-sm p-0.5" data-testid="dash-type-toggle" title="Counts follow each ticket's current type">
            {TYPE_PILLS.map(([val, label]) => (
              <button key={val} data-testid={`dash-type-${val.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setType(val)}
                className={`px-2.5 h-7 text-[11px] font-mono uppercase tracking-wider rounded-sm transition-colors ${dashType === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent/40"}`}>
                {label}
              </button>
            ))}
          </div>
          {user?.role === "admin" && (
            <div className="flex items-center gap-1 border border-border rounded-sm p-0.5" data-testid="dash-scope-toggle">
              {[["all", "All Members"], ["team", "My Team"]].map(([val, label]) => (
                <button key={val} data-testid={`dash-scope-${val}`} onClick={() => setScope(val)}
                  className={`px-2.5 h-7 text-[11px] font-mono uppercase tracking-wider rounded-sm transition-colors ${dashScope === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent/40"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <Link to="/incidents" data-testid="dash-open-queue" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">Open queue →</Link>
        </div>
      </div>

      {data.requester_responded > 0 && (
        <Link to="/incidents?view=responded" data-testid="dash-requester-responded-banner"
          className="flex items-center gap-2.5 mb-4 px-4 py-3 rounded-sm border border-rag-red/40 bg-rag-red-bg text-rag-red-fg hover:bg-rag-red/10 transition-colors">
          <ArrowBendUpLeft size={16} weight="bold" />
          <span className="text-sm font-mono font-bold uppercase tracking-wide">
            {data.requester_responded} ticket{data.requester_responded > 1 ? "s" : ""} — requestor responded, awaiting your reply
          </span>
          <span className="ml-auto text-xs font-mono opacity-70">View →</span>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Metric label={self ? `My ${typeWord}` : `Total ${typeWord}`} value={data.total} icon={Ticket} to={`/incidents${dashType !== "all" ? `?type=${encodeURIComponent(dashType)}` : ""}${self && user?.id ? `${dashType !== "all" ? "&" : "?"}assignee_id=${user.id}` : ""}`} />
        <Metric label={self ? "My Open" : "Open"} value={data.open} icon={Timer} tone="text-rag-amber-fg" to={self && user?.id ? `/incidents?view=open&assignee_id=${user.id}` : "/incidents?view=open"} />
        <Metric label={self ? "On Hold by Me" : "On Hold"} value={data.on_hold} icon={PauseCircle} tone={data.on_hold ? "text-muted-foreground" : ""} to={self && user?.id ? `/incidents?view=on_hold&assignee_id=${user.id}` : "/incidents?view=on_hold"} />
        <Metric label="Unassigned" value={data.unassigned} hint={self ? "Shared queue" : undefined} icon={UserMinus} tone={data.unassigned ? "text-rag-amber-fg" : ""} to="/incidents?view=unassigned" />
        <Metric label={self ? "Resolved by Me" : "Resolved"} value={data.resolved} icon={CheckCircle} tone="text-rag-green-fg" to={self && user?.id ? `/incidents?view=resolved&assignee_id=${user.id}` : "/incidents?view=resolved"} />
        <Metric label={self ? "My SLA Breaches" : "SLA Breached"} value={data.breached} icon={WarningOctagon} tone={data.breached ? "text-rag-red-fg" : ""} to={self && user?.id ? `/incidents?view=breached&assignee_id=${user.id}` : "/incidents?view=breached"} />
        <Metric label="Overdue (open)" value={data.overdue ?? 0} hint="Open, past resolution SLA" icon={WarningOctagon} tone={data.overdue ? "text-rag-red-fg" : ""} to="/incidents?view=breached" />
        <Metric label={self ? "My Response (hrs)" : "Avg Response (hrs)"} value={data.mtta_hours ?? 0} hint="Time to first response" icon={ClockCountdown} />
        <Metric label={self ? "My Resolution (hrs)" : "Avg Resolution (hrs)"} value={data.mttr_hours} hint="Time to resolve" icon={TrendUp} />
      </div>

      <Card className="border-border rounded-sm overflow-hidden" data-testid="my-open-tickets">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks size={16} className="text-primary" />
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{self ? "My Work Queue" : "Open Work Queue"}</div>
              <div className="font-display text-lg">{self ? "My Open Tickets" : "Open Tickets"} <span className="text-sm text-muted-foreground font-mono">· next SLA due first</span></div>
            </div>
          </div>
          <Link to="/incidents?view=open" data-testid="my-open-all" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">View all →</Link>
        </div>
        {myOpen.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">{self ? "You have no open tickets. Nice and clear!" : "No open tickets."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                <th className="text-left px-5 py-3">Ticket</th><th className="text-left px-3 py-3">Priority</th><th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Response due</th><th className="text-left px-3 py-3">Resolution due</th>
              </tr></thead>
              <tbody>
                {myOpen.map(t => {
                  const resp = timeUntil(t.sla_response_due_at);
                  const res = timeUntil(t.sla_resolution_due_at);
                  const paused = t.status === "on_hold";
                  return (
                    <tr key={t.id} className="border-b border-border/60 hover:bg-muted/20" data-testid={`my-open-row-${t.number}`}>
                      <td className="px-5 py-3">
                        <Link to={`/incidents/${t.id}`} className="font-mono text-primary hover:underline">{t.number}</Link>
                        <span className="text-muted-foreground ml-2 max-w-[280px] truncate inline-block align-bottom">{t.title}</span>
                      </td>
                      <td className="px-3 py-3"><span className={`text-xs font-mono px-1.5 py-0.5 border ${priorityColor(t.priority)}`}>{t.priority}</span></td>
                      <td className="px-3 py-3"><span className={`text-xs font-mono px-1.5 py-0.5 border ${statusColor(t.status)}`}>{(t.status || "").replace('_', ' ')}</span></td>
                      <td className="px-3 py-3 font-mono text-xs">{t.responded_at ? <span className="text-rag-green-fg">Met</span> : <span className={resp.breach ? "text-rag-red-fg" : "text-rag-amber-fg"}>{resp.text}</span>}</td>
                      <td className="px-3 py-3 font-mono text-xs">{paused ? <span className="text-muted-foreground">Paused</span> : <span className={t.breached ? "text-rag-red-fg" : "text-rag-amber-fg"}>{res.text}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {!self && agentPerf.length > 0 && (
        <Card className="border-border rounded-sm overflow-hidden" data-testid="agent-performance-table">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListChecks size={16} className="text-primary" />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Team Performance</div>
                <div className="font-display text-lg">Agent Metrics <span className="text-sm text-muted-foreground font-mono">· one row per agent</span></div>
              </div>
            </div>
            <Link to="/reports" data-testid="agent-perf-reports" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">Full reports →</Link>
          </div>
          <div className="p-3 border-b border-border flex items-center justify-between gap-2 flex-wrap" data-testid="agent-perf-controls">
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-1">Range</span>
              {[{ k: "7d", l: "7d" }, { k: "30d", l: "30d" }, { k: "90d", l: "90d" }, { k: "all", l: "All" }].map(r => (
                <button key={r.k} data-testid={`agent-perf-range-${r.k}`} onClick={() => setRange(r.k)}
                  className={`text-[10px] font-mono uppercase tracking-widest border rounded-sm px-2.5 py-1.5 ${perfRange === r.k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{r.l}</button>
              ))}
            </div>
            <button data-testid="agent-perf-export" onClick={exportPerfCsv} className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary border border-border rounded-sm px-2.5 py-1.5">
              <FileCsv size={13} /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                {PERF_COLS.map((c, ci) => (
                  <th key={c.key} onClick={() => toggleSort(c.key)} data-testid={`agent-perf-sort-${c.key}`}
                    className={`${c.align === "right" ? "text-right" : "text-left"} ${ci === 0 ? "px-5" : "px-3"} ${ci === PERF_COLS.length - 1 ? "pr-5" : ""} py-3 cursor-pointer select-none hover:text-primary`}>
                    <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                      {c.label}
                      {perfSort.key === c.key && (perfSort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
                    </span>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {sortedPerf.map(a => (
                  <tr key={a.agent_id} className="border-b border-border/60 hover:bg-muted/20" data-testid={`agent-perf-row-${a.agent_id}`}>
                    <td className="px-5 py-3">
                      <span className="font-medium">{a.name}</span>
                      <span className="text-[10px] font-mono uppercase text-muted-foreground ml-2">{a.role}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono">{a.assigned}</td>
                    <td className="px-3 py-3 text-right font-mono text-rag-amber-fg">{a.open}</td>
                    <td className="px-3 py-3 text-right font-mono text-muted-foreground">{a.on_hold}</td>
                    <td className="px-3 py-3 text-right font-mono text-rag-green-fg">{a.resolved}</td>
                    <td className={`px-3 py-3 text-right font-mono ${a.breached ? "text-rag-red-fg" : "text-muted-foreground"}`}>{a.breached}</td>
                    <td className="px-3 py-3 text-right font-mono">{a.avg_response_hrs}</td>
                    <td className="px-3 py-3 text-right font-mono">{a.avg_resolution_hrs}</td>
                    <td className="px-3 py-3 text-right font-mono text-primary">{a.avg_achieved_resolution_hrs}</td>
                    <td className={`px-5 py-3 text-right font-mono ${a.sla_compliance >= 90 ? "text-rag-green-fg" : a.sla_compliance >= 70 ? "text-rag-amber-fg" : "text-rag-red-fg"}`}>{a.sla_compliance}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 border-border rounded-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Ticket Volume — Last 7 days</div>
              <div className="font-display text-lg">Inflow Trend</div>
              <div className="text-[10px] font-mono text-muted-foreground mt-0.5">Click a day to see its tickets</div>
            </div>
            <div className="text-xs font-mono text-muted-foreground">SLA compliance <span className="text-primary">{data.sla_compliance}%</span></div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.trend_7d} style={{ cursor: "pointer" }} onClick={(e) => e && e.activeLabel && openDay(e.activeLabel)}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="date" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} tickFormatter={(d)=>d.slice(5)} />
              <YAxis stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} allowDecimals={false} />
              <Tooltip contentStyle={chart.tooltip} />
              <Line type="monotone" dataKey="count" stroke="hsl(48 100% 51%)" strokeWidth={2} dot={{ r: 4, fill: 'hsl(48 100% 51%)', cursor: 'pointer' }} activeDot={{ r: 6, cursor: 'pointer' }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">By Priority</div>
          <div className="font-display text-lg mb-3">Severity Mix</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={prioData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {prioData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip contentStyle={chart.tooltip} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-3 mt-2">
            {prioData.map(p => (
              <div key={p.name} className="flex items-center gap-1 text-xs font-mono">
                <span className="h-2 w-2" style={{ background: p.fill }} />{p.name} · {p.value}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {mailHealth?.configured && (
        <Card className="p-6 border-border rounded-sm" data-testid="mail-health-widget">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <span className={`h-2.5 w-2.5 rounded-full ${mailHealth.healthy ? "bg-rag-green" : "bg-rag-red"}`} />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Office 365 Email Pull</div>
                <div className="font-display text-lg">{mailHealth.healthy ? "Flowing" : "Attention needed"}</div>
              </div>
            </div>
            <div className="flex items-center gap-8 text-sm">
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Last poll</div>
                <div className="font-mono" data-testid="mail-health-last">{timeAgo(mailHealth.last_success_at)}</div>
              </div>
              <Link to="/incidents" className="group" data-testid="mail-health-today">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">From email today</div>
                <div className="font-display text-xl group-hover:text-primary transition-colors">{mailHealth.tickets_today}</div>
              </Link>
              <div className="max-w-xs">
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Mailbox</div>
                <div className="font-mono text-xs text-primary break-all">{mailHealth.mailbox}</div>
              </div>
            </div>
          </div>
          {!mailHealth.healthy && mailHealth.last_error && (
            <div className="mt-3 text-xs font-mono text-rag-red-fg bg-rag-red-bg border border-rag-red/30 rounded-sm px-3 py-2" data-testid="mail-health-error">
              Last error {mailHealth.last_error_at ? `(${timeAgo(mailHealth.last_error_at)})` : ""}: {mailHealth.last_error}
            </div>
          )}
        </Card>
      )}

      <Card className="p-6 border-border rounded-sm" data-testid="type-breakdown-widget">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">By Type</div>
        <div className="font-display text-lg mb-4">Ticket Composition</div>
        {typeData.length === 0 ? (
          <div className="text-sm text-muted-foreground font-mono">No tickets yet.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex w-full h-3 rounded-sm overflow-hidden">
              {typeData.map(t => (
                <div key={t.name} style={{ width: `${(t.value / typeTotal) * 100}%`, background: t.fill }} title={`${t.name}: ${t.value}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {typeData.map(t => (
                <Link
                  key={t.name}
                  to="/incidents"
                  data-testid={`type-stat-${t.name.toLowerCase().replace(/\s/g,'-')}`}
                  className="flex items-center justify-between border border-border rounded-sm px-3 py-2 hover:border-primary/60 transition-colors"
                >
                  <span className="flex items-center gap-2 text-xs font-mono">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.fill }} />
                    {t.name}
                  </span>
                  <span className="font-display text-lg">{t.value}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 border-border rounded-sm">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{self ? "My Throughput" : "Agent Performance"}</div>
        <div className="font-display text-lg mb-3">{self ? "Assigned vs Resolved" : "Load Distribution"}</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.agent_performance}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="name" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
            <YAxis stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} allowDecimals={false} />
            <Tooltip contentStyle={chart.tooltip} />
            <Bar dataKey="assigned" fill="hsl(240 5% 45%)" name="Assigned" />
            <Bar dataKey="resolved" fill="hsl(48 100% 51%)" name="Resolved" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {notif && (
        <Card className="p-6 border-border rounded-sm" data-testid="notif-activity-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={16} className="text-primary" />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Notifications Activity</div>
                <div className="font-display text-lg">Email Delivery — Last 24h</div>
              </div>
            </div>
            <Link to="/notifications" data-testid="notif-widget-link" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">Configure →</Link>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="border border-border rounded-sm p-4" data-testid="notif-sent">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2"><Check size={13} className="text-rag-green-fg" />Sent</div>
              <div className="font-display text-3xl text-rag-green-fg">{notif.counts.sent}</div>
            </div>
            <div className="border border-border rounded-sm p-4" data-testid="notif-simulated">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2"><Circle size={13} className="text-muted-foreground" />Simulated</div>
              <div className="font-display text-3xl text-muted-foreground">{notif.counts.simulated}</div>
            </div>
            <div className="border border-border rounded-sm p-4" data-testid="notif-failed">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2"><XCircle size={13} className={notif.counts.failed ? "text-rag-red-fg" : "text-muted-foreground"} />Failed</div>
              <div className={`font-display text-3xl ${notif.counts.failed ? "text-rag-red-fg" : ""}`}>{notif.counts.failed}</div>
            </div>
          </div>
          {notif.counts.simulated > 0 && notif.counts.sent === 0 && (
            <div className="text-[11px] font-mono text-rag-amber-fg mb-3">Delivery is in simulation mode — configure Microsoft 365 email delivery on the Email Notifications page to send real emails.</div>
          )}
          {notif.recent.length === 0 ? (
            <div className="text-sm text-muted-foreground">No email activity in the last 24 hours.</div>
          ) : (
            <div className="divide-y divide-border/60">
              {notif.recent.map(l => (
                <div key={l.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className="font-mono text-xs uppercase text-muted-foreground w-36">{l.event_type}</span>
                  <span className="text-xs text-muted-foreground w-52 truncate">{l.to}</span>
                  <span className={`text-xs font-mono ${l.status === "sent" ? "text-rag-green-fg" : l.status === "failed" ? "text-rag-red-fg" : "text-muted-foreground"}`}>{l.status}</span>
                  <span className="text-[10px] font-mono text-muted-foreground ml-auto" title={fmtIST(l.created_at)}>{humanTime(l.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Tickets created on {dayDetail?.date}</DialogTitle>
            <DialogDescription>
              {dayDetail?.loading ? "Loading…" : `${dayDetail?.rows?.length || 0} ticket(s) opened this day.`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {!dayDetail?.loading && (dayDetail?.rows?.length ? (
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                  <th className="text-left py-2 pr-3">Number</th><th className="text-left py-2 pr-3">Title</th><th className="text-left py-2 pr-3">Priority</th><th className="text-left py-2">Status</th>
                </tr></thead>
                <tbody>{dayDetail.rows.map(r => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-muted/20">
                    <td className="py-2 pr-3"><Link to={`/incidents/${r.id}`} data-testid={`day-detail-row-${r.number}`} onClick={() => setDayDetail(null)} className="font-mono text-primary hover:underline">{r.number}</Link></td>
                    <td className="py-2 pr-3 max-w-xs truncate">{r.title}</td>
                    <td className="py-2 pr-3"><span className={`text-xs font-mono px-1.5 py-0.5 border ${priorityColor(r.priority)}`}>{r.priority}</span></td>
                    <td className="py-2"><span className={`text-xs font-mono px-1.5 py-0.5 border ${statusColor(r.status)}`}>{(r.status || "").replace('_', ' ')}</span></td>
                  </tr>
                ))}</tbody>
              </table>
            ) : (
              <div className="text-sm text-muted-foreground py-6 text-center">No tickets were created on this day.</div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
